import * as React from 'react';

import { ScaleTicketTab } from '@rematter/modules/trade/types/scaleTickets';
import { AsyncBoundary } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $scaleTicketTab } from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { TicketInfoForm } from '../shared/TicketInfoForm';

import { InboundTicketTabDocuments } from './InboundTicketTabDocuments';
import { InboundTicketTabMaterials } from './InboundTicketTabMaterials';
import { InboundTicketTabNotes } from './InboundTicketTabNotes';
import { InboundTicketTabPhotos } from './InboundTicketTabPhotos';
import { InboundTicketTabSupplierInfo } from './InboundTicketTabSupplierInfo';

import { SectionErrorBoundary } from '~/app/components/error/SectionErrorBoundary';
import { PurchaseLoadTabCompliance } from '~/modules/trade/_components/purchase-loads/PurchaseLoadTabCompliance';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';

type InboundTicketTabsProps = {
  ticket: PurchaseLoadRecord;
  header?: React.ReactNode;
  isLoading?: boolean;
};

type TabContent = Partial<Record<ScaleTicketTab, React.ReactNode>>;

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET TABS ⚛️ ⚛️ ⚛️
//
export const InboundTicketTabs = ({
  ticket,
  header,
  isLoading,
}: InboundTicketTabsProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const tab = useAtomValue($scaleTicketTab);

  const status = ticket?.status;
  const activeTab = (tab || 'materials') as ScaleTicketTab;
  const isEditable = status !== 'Paid' && status !== 'Voided';

  // Memoized values
  const tabContent = React.useMemo(
    (): TabContent => ({
      materials: <InboundTicketTabMaterials ticket={ticket} />,
      notes: <InboundTicketTabNotes />,
      photos: <InboundTicketTabPhotos ticket={ticket} />,
      supplierInfo: (
        <InboundTicketTabSupplierInfo ticket={ticket} disabled={!isEditable} />
      ),
      history: (
        <div className="p-4 text-foreground/50">
          {t.scaleTicketPro.detail.historyComingSoon}
        </div>
      ),
      details: <TicketInfoForm ticket={ticket} disabled={!isEditable} />,
      documents: <InboundTicketTabDocuments ticket={ticket} />,
      files: (
        <div className="p-4 text-foreground/50">
          {t.scaleTicketPro.detail.filesComingSoon}
        </div>
      ),
      compliance: <PurchaseLoadTabCompliance />,
    }),
    [t, ticket, isEditable],
  );

  return (
    <AsyncBoundary
      error={null}
      loading={isLoading}
      className="@InboundTicketTabs"
    >
      {header}
      <SectionErrorBoundary key={activeTab}>
        <div className="min-h-0 flex-1">{tabContent[activeTab]}</div>
      </SectionErrorBoundary>
    </AsyncBoundary>
  );
};
